import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';
import technicalMaintenanceApi from '../../api/technicalMaintenanceApi';
import { Button } from '../../components/ui/Button';
import {
  TechnicalMaintenancePageShell,
  StatCard,
  EmptyState,
  formatDisplayDate,
  fmtMoney,
  statusBadgeClass,
} from './technicalMaintenanceUi';

const TechnicalMaintenanceEquipmentDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await technicalMaintenanceApi.getEquipmentDetail(id);
        setDetail(response?.data || response);
      } catch (err) {
        setError(err.message || 'Ekipman detayı yüklenemedi.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const equipment = detail?.equipment;
  const stats = detail?.stats || {};
  const records = detail?.maintenance_records || [];

  if (loading) {
    return (
      <TechnicalMaintenancePageShell title="Ekipman Detayı" subtitle="Yükleniyor...">
        <div className="tm-kpi-grid">
          {[1, 2, 3, 4].map((i) => <div key={i} className="tm-skeleton" />)}
        </div>
      </TechnicalMaintenancePageShell>
    );
  }

  if (error || !equipment) {
    return (
      <TechnicalMaintenancePageShell
        title="Ekipman Detayı"
        backTo="/internal-audit/technical-maintenance/equipment"
        backLabel="Envanter"
      >
        <div className="text-red-600 text-sm mb-4">{error || 'Ekipman bulunamadı.'}</div>
      </TechnicalMaintenancePageShell>
    );
  }

  return (
    <TechnicalMaintenancePageShell
      backTo="/internal-audit/technical-maintenance/equipment"
      backLabel="Envanter"
      actions={(
        <Button onClick={() => navigate(`/internal-audit/technical-maintenance/maintenance?equipment=${equipment.id}`)}>
          <PlusIcon className="h-4 w-4 mr-1 inline" />
          Yeni Bakım
        </Button>
      )}
    >
      <div className="tm-hero">
        <h1 className="tm-hero-title">{equipment.name}</h1>
        <div className="tm-hero-chips">
          {equipment.category_label ? <span className="tm-badge planned">{equipment.category_label}</span> : null}
          {equipment.area ? <span className="tm-badge area">{equipment.area}</span> : null}
          {equipment.equipment_code ? <span className="tm-badge area">{equipment.equipment_code}</span> : null}
          {!equipment.is_active ? <span className="tm-badge overdue">Pasif</span> : null}
        </div>
      </div>

      <div className="tm-kpi-grid">
        <StatCard label="Toplam Bakım" value={stats.total_maintenance_count ?? 0} accent="rgb(37 99 235)" />
        <StatCard label="Tamamlanan" value={stats.completed_count ?? 0} accent="rgb(5 150 105)" />
        <StatCard label="Son Bakım" value={formatDisplayDate(stats.latest_completed_date)} accent="rgb(124 58 237)" />
        <StatCard label="Sonraki Bakım" value={formatDisplayDate(stats.next_maintenance_date)} accent="rgb(217 119 6)" />
      </div>

      <div className="tm-info-grid">
        <div className="tm-info-item">
          <span className="tm-info-label">Konum</span>
          <span className="tm-info-value">{equipment.location_detail || '—'}</span>
        </div>
        <div className="tm-info-item">
          <span className="tm-info-label">Marka / Model</span>
          <span className="tm-info-value">{[equipment.brand, equipment.model_name].filter(Boolean).join(' ') || '—'}</span>
        </div>
        <div className="tm-info-item">
          <span className="tm-info-label">Toplam Maliyet</span>
          <span className="tm-info-value">{fmtMoney(stats.total_cost)} TRY</span>
        </div>
        {equipment.serial_number ? (
          <div className="tm-info-item">
            <span className="tm-info-label">Seri No</span>
            <span className="tm-info-value">{equipment.serial_number}</span>
          </div>
        ) : null}
        {equipment.maintenance_interval_days ? (
          <div className="tm-info-item">
            <span className="tm-info-label">Bakım Aralığı</span>
            <span className="tm-info-value">{equipment.maintenance_interval_days} gün</span>
          </div>
        ) : null}
        {equipment.warranty_expiry_date ? (
          <div className="tm-info-item">
            <span className="tm-info-label">Garanti Bitiş</span>
            <span className="tm-info-value">{formatDisplayDate(equipment.warranty_expiry_date)}</span>
          </div>
        ) : null}
        {equipment.temperature ? (
          <div className="tm-info-item">
            <span className="tm-info-label">Sıcaklık</span>
            <span className="tm-info-value">{equipment.temperature}</span>
          </div>
        ) : null}
        {equipment.notes ? (
          <div className="tm-info-item" style={{ gridColumn: '1 / -1' }}>
            <span className="tm-info-label">Not</span>
            <span className="tm-info-value">{equipment.notes}</span>
          </div>
        ) : null}
      </div>

      <div className="tm-dash-card">
        <h3>Bakım Geçmişi</h3>
        {records.length === 0 ? (
          <EmptyState message="Bu ekipman için henüz bakım kaydı yok." />
        ) : (
          <div className="tm-timeline">
            {records.map((record) => (
              <div key={record.id} className="tm-timeline-item">
                <div
                  className="tm-timeline-card"
                  onClick={() => navigate(`/internal-audit/technical-maintenance/maintenance?equipment=${equipment.id}`, { state: { editRecordId: record.id } })}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && navigate(`/internal-audit/technical-maintenance/maintenance?equipment=${equipment.id}`)}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                    <div>
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {record.title || record.maintenance_type_label}
                      </div>
                      <div className="text-sm text-gray-500 mt-0.5">
                        {formatDisplayDate(record.completed_date || record.scheduled_date)}
                        {record.performed_by ? ` · ${record.performed_by}` : ''}
                        {record.vendor ? ` · ${record.vendor}` : ''}
                      </div>
                    </div>
                    <span className={`tm-badge ${statusBadgeClass(record.status)}`}>
                      {record.status_label}
                    </span>
                  </div>
                  {record.description ? (
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">{record.description}</p>
                  ) : null}
                  {record.replaced_parts ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      <strong className="font-semibold text-gray-700 dark:text-gray-300">Parçalar:</strong> {record.replaced_parts}
                    </p>
                  ) : null}
                  <div className="flex flex-wrap gap-3 mt-3 text-xs text-gray-500">
                    {record.next_maintenance_date ? (
                      <span>Sonraki: {formatDisplayDate(record.next_maintenance_date)}</span>
                    ) : null}
                    {record.cost != null && record.cost !== '' ? (
                      <span>Maliyet: {fmtMoney(record.cost)} TRY</span>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </TechnicalMaintenancePageShell>
  );
};

export default TechnicalMaintenanceEquipmentDetailPage;
